import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { logUserEvent } from '../../lib/audit';
import { useAuth } from '../../contexts/AuthContext';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { WizardStep1 } from './components/WizardStep1';
import { WizardStep2 } from './components/WizardStep2';
import { WizardStep3 } from './components/WizardStep3';
import { WizardStep4 } from './components/WizardStep4';
import { WizardStep5 } from './components/WizardStep5';
import { WizardStep6 } from './components/WizardStep6';

export interface WizardFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  cpf: string;
  phone: string;
  currency: string;
  timezone: string;
  goal: string;
  hasBankAccount: boolean;
  hasCreditCard: boolean;
  setupNow: boolean;
  plan: 'free' | 'paid';
}

const TOTAL_STEPS = 6;
const STORAGE_KEY = 'wizard_progress';

const DEFAULT_FORM: WizardFormData = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  cpf: '',
  phone: '',
  currency: 'BRL',
  timezone: 'America/Sao_Paulo',
  goal: '',
  hasBankAccount: false,
  hasCreditCard: false,
  setupNow: false,
  plan: 'free',
};

const stepTitles = [
  'Dados de acesso',
  'Perfil pessoal',
  'Seu objetivo',
  'Configuração inicial',
  'Escolha seu plano',
  'Tudo pronto!',
];

function loadFromStorage(): { step: number; form: WizardFormData; userId: string | null } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveToStorage(step: number, form: WizardFormData, userId: string | null) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, form, userId }));
  } catch { /* ignore */ }
}

function clearStorage() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

// ── Roles that MUST NEVER go through the customer wizard ──────────────────────
const WIZARD_BLOCKED_ROLES = ['master_admin', 'admin', 'support', 'finance'];

/** Returns true if the given userId has an administrative role. */
async function isAdminUser(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId)
    .maybeSingle();
  return !!(data?.role && WIZARD_BLOCKED_ROLES.includes(data.role));
}

export default function CadastroPage() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const { settings } = useSystemSettings();

  const platformName = settings.platform_name || '';
  const logoUrl = settings.logo_small_url || settings.logo_url || 'https://public.readdy.ai/ai/img_res/651de865-f324-4bf8-91db-557422cf642b.png';
  const signupAllowed = settings.allow_public_signup !== 'false';

  const [currentStep, setCurrentStep] = useState(1);
  const [globalError, setGlobalError] = useState('');
  const [stepLoading, setStepLoading] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [formData, setFormData] = useState<WizardFormData>(DEFAULT_FORM);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);

  // Ref guard: prevents concurrent / double signUp calls
  const isSigningUpRef = useRef(false);

  // Reset googleLoading on bfcache restore or tab visibility regain
  useEffect(() => {
    const resetGoogleLoading = () => setGoogleLoading(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') resetGoogleLoading();
    };
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) resetGoogleLoading();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (!googleLoading) return;
    const timer = setTimeout(() => setGoogleLoading(false), 60_000);
    return () => clearTimeout(timer);
  }, [googleLoading]);

  // ─── Friendly error messages ─────────────────────────────────────────────────
  const getFriendlyAuthError = (message: string): string => {
    const msg = message.toLowerCase();
    if (
      msg.includes('rate limit') ||
      msg.includes('over_email_send_rate_limit') ||
      msg.includes('too many requests') ||
      msg.includes('email rate limit exceeded') ||
      msg.includes('for security purposes') ||
      msg.includes('request this after')
    ) {
      return 'Muitas tentativas em pouco tempo. Aguarde alguns minutos antes de tentar novamente.';
    }
    if (msg.includes('invalid login credentials') || msg.includes('invalid credentials')) {
      return 'E-mail ou senha incorretos.';
    }
    if (msg.includes('email not confirmed')) {
      return 'E-mail ainda não confirmado. Verifique sua caixa de entrada.';
    }
    if (msg.includes('user not found')) {
      return 'Usuário não encontrado.';
    }
    if (msg.includes('database error')) {
      return 'Erro interno ao criar conta. Tente novamente em alguns instantes.';
    }
    return message;
  };

  // States for email-already-exists flow
  const [emailExistsMode, setEmailExistsMode] = useState<
    'none' | 'exists_incomplete' | 'exists_complete' | 'recover' | 'recover_sent'
  >('none');
  const [resumePassword, setResumePassword] = useState('');
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState('');

  const updateForm = (updates: Partial<WizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Reset email-exists state when email changes
  const handleEmailChange = (updates: Partial<WizardFormData>) => {
    if ('email' in updates) {
      setEmailExistsMode('none');
      setResumeError('');
      setGlobalError('');
    }
    updateForm(updates);
  };

  // On mount: check auth state and resume wizard if needed
  const checkAuthAndResume = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      const userId = session.user.id;

      // ── ADMIN GUARD: admin users must never enter the customer wizard ─────────
      const adminBlocked = await isAdminUser(userId);
      if (adminBlocked) {
        navigate('/admin', { replace: true });
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('wizard_completed, wizard_step, name, full_name, phone, currency, timezone, goal, plan, has_bank_account, has_credit_card, account_status')
        .eq('id', userId)
        .maybeSingle();

      if (profile?.wizard_completed) {
        navigate('/dashboard', { replace: true });
        return;
      }

      // Detect Google provider
      const provider = session.user.app_metadata?.provider ?? '';
      const isGoogle = provider === 'google';
      setIsGoogleUser(isGoogle);

      // Extract Google metadata for pre-fill
      const meta = session.user.user_metadata ?? {};
      const googleFirstName = meta.given_name ?? meta.name?.split(' ')[0] ?? '';
      const googleLastName = meta.family_name ?? (meta.name?.split(' ').slice(1).join(' ')) ?? '';
      const googleEmail = session.user.email ?? '';
      const googleAvatar = meta.avatar_url ?? meta.picture ?? null;

      // Resume from saved step
      setCreatedUserId(userId);
      const savedStep = profile?.wizard_step ?? 2;
      setCurrentStep(Math.max(2, Math.min(savedStep, TOTAL_STEPS)));

      const stored = loadFromStorage();
      setFormData({
        ...DEFAULT_FORM,
        ...(stored?.form ?? {}),
        name: profile?.name ?? (isGoogle ? (meta.name ?? googleFirstName) : stored?.form?.name ?? ''),
        firstName: (profile as any)?.first_name ?? (isGoogle ? googleFirstName : stored?.form?.firstName ?? ''),
        lastName: (profile as any)?.last_name ?? (isGoogle ? googleLastName : stored?.form?.lastName ?? ''),
        cpf: (profile as any)?.cpf ?? stored?.form?.cpf ?? '',
        phone: profile?.phone ?? stored?.form?.phone ?? '',
        currency: profile?.currency ?? 'BRL',
        timezone: profile?.timezone ?? 'America/Sao_Paulo',
        goal: profile?.goal ?? stored?.form?.goal ?? '',
        plan: (profile?.plan as 'free' | 'paid') ?? 'free',
        hasBankAccount: profile?.has_bank_account ?? false,
        hasCreditCard: profile?.has_credit_card ?? false,
        password: stored?.form?.password ?? '',
        confirmPassword: stored?.form?.confirmPassword ?? '',
        email: stored?.form?.email ?? googleEmail,
        ...(isGoogle && !profile?.name && googleAvatar ? {} : {}),
      });

      // Update profile with Google avatar if not set
      if (isGoogle && googleAvatar) {
        supabase.from('profiles').update({ avatar_url: googleAvatar }).eq('id', userId).then(() => {});
      }
    } else {
      const stored = loadFromStorage();
      if (stored && stored.step > 1) {
        setFormData(stored.form);
      }
    }

    setChecking(false);
  }, [navigate]);

  useEffect(() => {
    checkAuthAndResume();
  }, [checkAuthAndResume]);

  useEffect(() => {
    if (!checking) {
      saveToStorage(currentStep, formData, createdUserId);
    }
  }, [currentStep, formData, createdUserId, checking]);

  const advanceStep = () => setCurrentStep((s) => s + 1);
  const goBack = () => setCurrentStep((s) => Math.max(1, s - 1));

  // ─── Step 1: Create auth user ────────────────────────────────────────────────
  const handleStep1 = async (): Promise<boolean> => {
    // Check if public signup is disabled
    if (!signupAllowed) {
      setGlobalError('O cadastro público está temporariamente desativado. Entre em contato com o suporte para criar uma conta.');
      return false;
    }

    // Hard guard: block concurrent calls (double-click, rapid submit, etc.)
    if (isSigningUpRef.current) return false;
    isSigningUpRef.current = true;

    try {
      setGlobalError('');
      setEmailExistsMode('none');

      // ── If we already created this user in the current session, just advance ──
      if (createdUserId) return true;

      // ── If there is already an active Supabase session, reuse it ──────────────
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      if (existingSession?.user) {
        const userId = existingSession.user.id;

        // ── ADMIN GUARD: never modify admin profile with wizard data ─────────────
        const adminBlocked = await isAdminUser(userId);
        if (adminBlocked) {
          navigate('/admin', { replace: true });
          return false;
        }

        setCreatedUserId(userId);
        // Update name in profile if provided
        await supabase
          .from('profiles')
          .update({ name: formData.name, wizard_step: 2, account_status: 'incomplete' })
          .eq('id', userId);
        return true;
      }

      // ── No existing user/session — proceed with signUp ────────────────────────
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: { data: { name: formData.name } },
      });

      if (error) {
        const errMsg = error.message.toLowerCase();
        if (
          errMsg.includes('rate limit') ||
          errMsg.includes('over_email_send_rate_limit') ||
          errMsg.includes('too many requests') ||
          errMsg.includes('for security purposes') ||
          errMsg.includes('request this after')
        ) {
          setGlobalError('Muitas tentativas em pouco tempo. Aguarde alguns minutos antes de tentar novamente.');
          return false;
        }

        if (
          errMsg.includes('already registered') ||
          errMsg.includes('already been registered') ||
          errMsg.includes('user already exists')
        ) {
          await detectExistingAccount(formData.email);
          return false;
        }

        setGlobalError(getFriendlyAuthError(error.message));
        return false;
      }

      // ── Silent duplicate: Supabase returns a user with empty identities
      // when email confirmation is ON and the email already exists.
      // In this case we must NOT treat it as a new user.
      if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
        await detectExistingAccount(formData.email);
        return false;
      }

      if (data.user) {
        setCreatedUserId(data.user.id);
        await supabase
          .from('profiles')
          .update({ name: formData.name, wizard_step: 2, wizard_completed: false, account_status: 'incomplete' })
          .eq('id', data.user.id);
      }
      return true;

    } finally {
      isSigningUpRef.current = false;
    }
  };

  // ─── Google signup from wizard ───────────────────────────────────────────────
  const handleGoogleSignup = async () => {
    if (!signupAllowed) {
      setGlobalError('O cadastro público está temporariamente desativado. Entre em contato com o suporte para criar uma conta.');
      return;
    }
    setGoogleLoading(true);
    setGlobalError('');
    const redirectTo = `${window.location.origin}/cadastro`;
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: { access_type: 'offline', prompt: 'select_account' },
      },
    });
    if (err) {
      const msg = err.message.toLowerCase();
      if (msg.includes('provider is not enabled') || msg.includes('unsupported provider')) {
        setGlobalError('Login com Google não está ativo. Use e-mail e senha ou fale com o suporte.');
      } else {
        setGlobalError('Não foi possível conectar com o Google. Tente novamente.');
      }
      setGoogleLoading(false);
    }
    // On success: browser redirects — googleLoading stays true intentionally
  };

  // Detect if existing account is incomplete or complete
  const detectExistingAccount = async (email: string) => {
    // Try to sign in with the email - we can't read their profile without auth
    // So we show "exists" state and let user provide password to check
    // This is the safest approach without service role
    setEmailExistsMode('exists_incomplete'); // default to incomplete; will verify on resume attempt
  };

  // Handle resuming an incomplete signup
  const handleResumeExisting = async () => {
    setResumeLoading(true);
    setResumeError('');

    const { data, error } = await supabase.auth.signInWithPassword({
      email: formData.email,
      password: resumePassword,
    });

    if (error) {
      setResumeError('Senha incorreta. Tente novamente.');
      setResumeLoading(false);
      return;
    }

    if (data.user) {
      // ── ADMIN GUARD: admin who mistakenly tries to resume goes to /admin ───────
      const adminBlocked = await isAdminUser(data.user.id);
      if (adminBlocked) {
        navigate('/admin', { replace: true });
        setResumeLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('wizard_completed, wizard_step, name, full_name, phone, currency, timezone, goal, plan, has_bank_account, has_credit_card')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profile?.wizard_completed) {
        // Account is fully complete — redirect to dashboard or login
        setEmailExistsMode('exists_complete');
        setResumeLoading(false);
        return;
      }

      // Incomplete — resume wizard
      setCreatedUserId(data.user.id);
      const savedStep = profile?.wizard_step ?? 2;
      setCurrentStep(Math.max(2, Math.min(savedStep, TOTAL_STEPS)));

      setFormData((prev) => ({
        ...prev,
        name: profile?.name ?? prev.name,
        firstName: (profile as any)?.first_name ?? prev.firstName,
        lastName: (profile as any)?.last_name ?? prev.lastName,
        cpf: (profile as any)?.cpf ?? prev.cpf,
        phone: profile?.phone ?? prev.phone,
        currency: profile?.currency ?? 'BRL',
        timezone: profile?.timezone ?? 'America/Sao_Paulo',
        goal: profile?.goal ?? prev.goal,
        plan: (profile?.plan as 'free' | 'paid') ?? 'free',
        hasBankAccount: profile?.has_bank_account ?? false,
        hasCreditCard: profile?.has_credit_card ?? false,
      }));

      setEmailExistsMode('none');
    }

    setResumeLoading(false);
  };

  // Handle password recovery
  const handlePasswordRecovery = async () => {
    setResumeLoading(true);
    setResumeError('');

    const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      const errMsg = error.message.toLowerCase();
      if (
        errMsg.includes('rate limit') ||
        errMsg.includes('over_email_send_rate_limit') ||
        errMsg.includes('too many requests') ||
        errMsg.includes('for security purposes') ||
        errMsg.includes('request this after')
      ) {
        setResumeError('Muitas tentativas em pouco tempo. Aguarde alguns minutos antes de tentar novamente.');
      } else {
        setResumeError(getFriendlyAuthError(error.message));
      }
    } else {
      setEmailExistsMode('recover_sent');
    }

    setResumeLoading(false);
  };

  // ─── Step 2: Save personal profile ──────────────────────────────────────────
  // Uses UPDATE (profile always exists after trigger in step 1)
  const handleStep2 = async (): Promise<boolean> => {
    if (!createdUserId) return true;
    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    const fullNameDerived = [firstName, lastName].filter(Boolean).join(' ') || formData.name;
    const { error } = await supabase
      .from('profiles')
      .update({
        first_name: firstName,
        last_name: lastName,
        full_name: fullNameDerived,
        cpf: formData.cpf.replace(/\D/g, ''),
        phone: formData.phone,
        currency: formData.currency,
        timezone: formData.timezone,
        wizard_step: 3,
        updated_at: new Date().toISOString(),
      })
      .eq('id', createdUserId);

    if (error) {
      setGlobalError(`Erro ao salvar perfil: ${error.message}`);
      return false;
    }
    return true;
  };

  // ─── Step 3: Save financial goal/objective ───────────────────────────────────
  const handleStep3 = async (): Promise<boolean> => {
    if (!createdUserId) return true;
    const { error } = await supabase
      .from('profiles')
      .update({
        goal: formData.goal,
        objective: formData.goal,
        wizard_step: 4,
        updated_at: new Date().toISOString(),
      })
      .eq('id', createdUserId);

    if (error) { setGlobalError('Erro ao salvar objetivo. Tente novamente.'); return false; }
    return true;
  };

  // ─── Step 4: Save initial setup preferences ──────────────────────────────────
  const handleStep4 = async (): Promise<boolean> => {
    if (!createdUserId) return true;
    const { error } = await supabase
      .from('profiles')
      .update({
        has_bank_account: formData.hasBankAccount,
        has_credit_card: formData.hasCreditCard,
        wizard_step: 5,
        updated_at: new Date().toISOString(),
      })
      .eq('id', createdUserId);

    if (error) { setGlobalError('Erro ao salvar preferências. Tente novamente.'); return false; }
    return true;
  };

  // ─── Step 5: Set subscription plan ──────────────────────────────────────────
  const handleStep5 = async (): Promise<boolean> => {
    if (!createdUserId) return true;

    // Map UI plan id to DB slug: 'paid' → 'pro', 'free' → 'free'
    const planSlug = formData.plan === 'paid' ? 'pro' : 'free';

    const { data: planData, error: planError } = await supabase
      .from('plans')
      .select('id, price_brl, periodicity')
      .eq('slug', planSlug)
      .eq('is_active', true)
      .maybeSingle();

    if (planError) {
      setGlobalError(`Erro ao buscar plano: ${planError.message}`);
      return false;
    }

    if (!planData?.id) {
      // Fallback: try to get any active plan matching the type
      const { data: fallbackPlan } = await supabase
        .from('plans')
        .select('id, price_brl, periodicity, slug')
        .eq('is_active', true)
        .order('price_brl', { ascending: formData.plan === 'free' })
        .limit(1)
        .maybeSingle();

      if (!fallbackPlan?.id) {
        setGlobalError('Nenhum plano disponível. Entre em contato com o suporte.');
        return false;
      }

      // Use fallback plan
      return await saveSubscription(createdUserId, fallbackPlan.id, fallbackPlan.price_brl, fallbackPlan.periodicity);
    }

    return await saveSubscription(createdUserId, planData.id, planData.price_brl, planData.periodicity);
  };

  const saveSubscription = async (
    userId: string,
    planId: string,
    _priceBrl: number,
    _periodicity: string
  ): Promise<boolean> => {
    // Use the new RPC that handles trial + annual pricing automatically
    const { data, error } = await supabase.rpc('upgrade_plan_with_trial', {
      p_plan_id: planId,
      p_billing_cycle: 'monthly',
      p_force_paid: false,
    });

    if (error) {
      setGlobalError(`Erro ao configurar plano: ${error.message}`);
      return false;
    }

    const result = data as { success: boolean; error?: string };
    if (!result.success) {
      setGlobalError(result.error || 'Erro ao configurar plano. Tente novamente.');
      return false;
    }

    await supabase
      .from('profiles')
      .update({ wizard_step: 6, updated_at: new Date().toISOString() })
      .eq('id', userId);

    return true;
  };

  // ─── Step 6: Mark wizard completed ──────────────────────────────────────────
  const handleFinish = async () => {
    setFinishLoading(true);
    try {
      if (createdUserId) {
        // ── ADMIN GUARD: never mark admin as wizard_completed via customer flow ───
        const adminBlocked = await isAdminUser(createdUserId);
        if (adminBlocked) {
          clearStorage();
          navigate('/admin', { replace: true });
          return;
        }

        await supabase
          .from('profiles')
          .update({
            wizard_completed: true,
            wizard_step: 6,
            account_status: 'active',
            updated_at: new Date().toISOString(),
          })
          .eq('id', createdUserId);

        // ── Log onboarding completion with EXACT timestamp ───────────────────────
        // Must happen IMMEDIATELY after wizard_completed=true so the audit_log
        // created_at reflects the real completion time, not any future profile update.
        await logUserEvent(createdUserId, 'onboarding_complete', {
          plan: formData.plan,
          provider: isGoogleUser ? 'google' : 'email',
        });

        // ── Send welcome notification if enabled ─────────────────────────────────
        const welcomeEnabled = settings.welcome_notification_enabled !== 'false';
        if (welcomeEnabled) {
          const firstName = formData.firstName?.trim() || formData.name?.split(' ')[0] || '';
          const rawTitle = settings.welcome_notification_title ||
            `Bem-vindo(a)${firstName ? `, ${firstName}` : ''} à plataforma! 🎉`;
          const rawBody = settings.welcome_notification_body ||
            'Sua conta foi criada com sucesso. Explore o dashboard e comece a organizar suas finanças agora mesmo!';

          // Replace {nome} placeholder with user's first name
          const title = rawTitle.replace(/\{nome\}/gi, firstName || 'você');
          const body  = rawBody.replace(/\{nome\}/gi, firstName || 'você');

          await supabase.from('notifications').insert({
            user_id: createdUserId,
            type: 'welcome',
            title,
            body,
            data: {},
          });
        }
      }
      // Refresh the AuthContext profile so ProtectedRoute sees wizard_completed = true
      // before we navigate — this prevents the redirect loop back to /cadastro
      await refreshProfile();
      clearStorage();
      navigate('/dashboard', { replace: true });
    } catch {
      // Even if something fails, try to navigate anyway
      clearStorage();
      navigate('/dashboard', { replace: true });
    } finally {
      setFinishLoading(false);
    }
  };

  const makeAsyncNext = (handler: () => Promise<boolean>) => async () => {
    setGlobalError('');
    setStepLoading(true);
    try {
      const ok = await handler();
      if (ok) advanceStep();
    } finally {
      setStepLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-emerald-500" />
          <p className="text-sm text-gray-500">Carregando...</p>
        </div>
      </div>
    );
  }

  const steps: Record<number, React.ReactNode> = {
    1: (
      <WizardStep1
        formData={formData}
        onChange={handleEmailChange}
        onNext={async () => {
          setGlobalError('');
          setStepLoading(true);
          const ok = await handleStep1();
          setStepLoading(false);
          if (ok) advanceStep();
        }}
        error={globalError}
        loading={stepLoading}
        emailExistsMode={emailExistsMode}
        resumePassword={resumePassword}
        onResumePasswordChange={setResumePassword}
        onResumeExisting={handleResumeExisting}
        onPasswordRecovery={handlePasswordRecovery}
        onGoToLogin={async () => {
          // Sign out any active session so the user lands on a clean login page.
          // Without this, the LandingPage detects an existing session and immediately
          // redirects to /dashboard → ProtectedRoute → back to /cadastro (loop).
          try {
            await supabase.auth.signOut();
          } catch { /* ignore */ }
          clearStorage();
          navigate('/');
        }}
        onResetEmailExistsMode={() => { setEmailExistsMode('none'); setResumeError(''); }}
        resumeLoading={resumeLoading}
        resumeError={resumeError}
        onGoogleSignup={handleGoogleSignup}
        googleLoading={googleLoading}
      />
    ),
    2: (
      <WizardStep2
        formData={formData}
        onChange={updateForm}
        onNext={makeAsyncNext(handleStep2)}
        onBack={goBack}
        loading={stepLoading}
        error={globalError}
      />
    ),
    3: (
      <WizardStep3
        formData={formData}
        onChange={updateForm}
        onNext={makeAsyncNext(handleStep3)}
        onBack={goBack}
        loading={stepLoading}
        error={globalError}
      />
    ),
    4: (
      <WizardStep4
        formData={formData}
        onChange={updateForm}
        onNext={makeAsyncNext(handleStep4)}
        onBack={goBack}
        loading={stepLoading}
        error={globalError}
      />
    ),
    5: (
      <WizardStep5
        formData={formData}
        onChange={updateForm}
        onNext={makeAsyncNext(handleStep5)}
        onBack={goBack}
        loading={stepLoading}
        error={globalError}
      />
    ),
    6: <WizardStep6 formData={formData} onFinish={handleFinish} loading={finishLoading} />,
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-white flex flex-col items-center justify-center p-6"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      <div className="flex items-center gap-2.5 mb-8">
        <img
          src={logoUrl}
          alt={platformName}
          className="w-9 h-9 rounded-lg object-cover"
        />
        <span className="text-lg font-700 text-gray-900">
          {platformName}
        </span>
      </div>

      {/* Google user badge */}
      {isGoogleUser && currentStep > 1 && (
        <div className="w-full max-w-[520px] mb-4 flex items-center gap-2.5 px-4 py-2.5 bg-white border border-gray-200 rounded-xl">
          <img
            src="https://readdy.ai/api/search-image?query=Google%20G%20logo%20colorful%20red%20blue%20yellow%20green%20simple%20icon%20flat%20design%20transparent%20background&width=20&height=20&seq=google_logo_badge&orientation=squarish"
            alt="Google"
            className="w-4 h-4 object-contain shrink-0"
          />
          <span className="text-[13px] text-gray-600 font-500">
            Conta criada via Google
            {formData.email && (
              <span className="text-gray-400 font-400"> · {formData.email}</span>
            )}
          </span>
          <div className="ml-auto w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
        </div>
      )}

      {/* Signup disabled banner */}
      {!signupAllowed && currentStep === 1 && (
        <div className="w-full max-w-[520px] mb-4 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <i className="ri-lock-line text-amber-500 text-base shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-600 text-amber-800">Cadastro temporariamente desativado</p>
            <p className="text-[12px] text-amber-600 mt-0.5">Novos cadastros estão suspensos no momento. Entre em contato com o suporte para mais informações.</p>
          </div>
        </div>
      )}

      {currentStep < TOTAL_STEPS && (
        <div className="w-full max-w-[520px] mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-500 text-gray-500">
              Etapa {currentStep} de {TOTAL_STEPS - 1}
            </span>
            <span className="text-sm font-600 text-emerald-600">{stepTitles[currentStep - 1]}</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / (TOTAL_STEPS - 1)) * 100}%` }}
            />
          </div>
          <div className="flex justify-between mt-3">
            {Array.from({ length: TOTAL_STEPS - 1 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-700 transition-all ${
                    i + 1 < currentStep
                      ? 'bg-emerald-500 text-white'
                      : i + 1 === currentStep
                      ? 'bg-emerald-500 text-white ring-4 ring-emerald-100'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {i + 1 < currentStep ? <i className="ri-check-line text-xs" /> : i + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="w-full max-w-[520px] bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {steps[currentStep]}
      </div>

      {currentStep === 1 && emailExistsMode === 'none' && (
        <p className="mt-6 text-sm text-gray-500">
          Já tem conta?{' '}
          <button
            onClick={async () => {
              try { await supabase.auth.signOut(); } catch { /* ignore */ }
              clearStorage();
              navigate('/');
            }}
            className="text-emerald-600 font-600 hover:underline cursor-pointer whitespace-nowrap"
          >
            Fazer login
          </button>
        </p>
      )}
    </div>
  );
}
